import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/api";
import { GstVerify } from "@/components/GstVerify";
import { toast } from "sonner";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

const FIELDS = [
  ["contact_person", "Contact person", "text", "e.g. Ramesh Agarwal"],
  ["phone", "Phone", "text", "10 digit mobile"],
  ["whatsapp", "WhatsApp number", "text", "10 digit, used for sharing"],
  ["alt_phone", "Alternate phone", "text", "Office / landline"],
  ["email", "Email", "email", "name@company.com"],
  ["legal_name", "Legal name (GST)", "text", "As per GST records"],
  ["trade_name", "Trade name (GST)", "text", "As per GST records"],
  ["city", "City", "text", "e.g. Muzaffarpur"],
  ["state", "State", "text", "e.g. Bihar"],
  ["pincode", "Pincode", "text", "e.g. 842001"],
  ["transport", "Transport", "text", "Regular transporter"],
  ["credit_days", "Credit days", "number", "e.g. 30"],
  ["credit_limit", "Credit limit ₹", "number", "e.g. 500000"],
  ["opening_balance", "Opening balance ₹", "number", "e.g. 25000"],
];

const empty = FIELDS.reduce((a, [k]) => ({ ...a, [k]: "" }), {
  address: "", notes: "", gstin: "", gst_status: "", gst_verified_at: "",
});

export const CustomerDetailsDialog = ({ customer, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    setLoading(true);
    api
      .get(`/customers/${encodeURIComponent(customer.name)}/profile`)
      .then(({ data }) => setForm({ ...empty, ...Object.fromEntries(Object.entries(data).filter(([k]) => k in empty)) }))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, customer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/customers/${encodeURIComponent(customer.name)}/profile`, {
        ...form,
        credit_days: parseFloat(form.credit_days) || 0,
        credit_limit: parseFloat(form.credit_limit) || 0,
        opening_balance: parseFloat(form.opening_balance) || 0,
      });
      toast.success("Customer details saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="customer-details-dialog"
        className="left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 p-0 sm:max-w-none sm:rounded-none lg:w-[calc(100vw-var(--sb-w))]"
      >
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8">
          <DialogHeader>
            <DialogTitle className="font-head text-3xl font-extrabold tracking-tighter">Customer details</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">{customer?.name}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <p className="py-12 text-center text-sm text-zinc-500">Loading…</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <GstVerify
                testId="customer"
                value={form.gstin}
                onChange={(v) => setForm((f) => ({ ...f, gstin: v }))}
                onVerified={(d) =>
                  setForm((f) => ({
                    ...f,
                    gstin: d.gstin,
                    legal_name: d.legal_name || f.legal_name,
                    trade_name: d.trade_name || f.trade_name,
                    address: d.address || f.address,
                    city: d.city || f.city,
                    state: d.state || f.state,
                    pincode: d.pincode || f.pincode,
                    gst_status: d.status || "",
                    gst_verified_at: new Date().toISOString(),
                  }))
                }
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {FIELDS.map(([key, label, type, placeholder]) => (
                  <div key={key}>
                    <L>{label}</L>
                    <Input
                      data-testid={`customer-${key.replace(/_/g, "-")}-input`}
                      type={type}
                      step={type === "number" ? "any" : undefined}
                      className="mt-2 rounded-sm"
                      value={form[key] ?? ""}
                      onChange={set(key)}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>

              <div>
                <L>Address</L>
                <Textarea
                  data-testid="customer-address-input"
                  rows={3}
                  className="mt-2 rounded-sm"
                  value={form.address ?? ""}
                  onChange={set("address")}
                  placeholder="Shop / godown address"
                />
              </div>

              <div>
                <L>Notes</L>
                <Textarea
                  data-testid="customer-notes-input"
                  rows={3}
                  className="mt-2 rounded-sm"
                  value={form.notes ?? ""}
                  onChange={set("notes")}
                  placeholder="Payment habits, preferences, remarks…"
                />
              </div>

              <p className="border-l-2 border-[#C8A03C] bg-amber-50/60 p-2 text-xs text-zinc-600">
                GSTIN and address here are printed on this customer's bills. Company-wise discounts are set from the
                <strong> Company discount</strong> button.
              </p>

              <Button
                data-testid="customer-details-save-button"
                type="submit"
                disabled={saving}
                className="w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              >
                {saving ? "Saving…" : "Save customer details"}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
